// src/components/quality/charts/AssessmentsByCriteriaChart.js
import { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const AssessmentsByCriteriaChart = ({ assessments, criteria, fieldOptions }) => {
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    if (!assessments || !criteria || !fieldOptions) return;

    // Группируем оценки по критериям
    const byCriteria = {};
    
    // Инициализируем данные для всех критериев
    criteria.forEach(criterion => {
      byCriteria[criterion.id] = {
        id: criterion.id,
        name: criterion.name,
        zeroCount: 0
      };
    });

    // Подсчитываем количество нулевых оценок для каждого критерия
    assessments.forEach(assessment => {
      if (assessment.items) {
        assessment.items.forEach(item => {
          if (item.criteria_id && byCriteria[item.criteria_id] !== undefined) {
            if (item.score === 0) {
              byCriteria[item.criteria_id].zeroCount++;
            }
          }
        });
      }
    });

    // Преобразуем в массив и сортируем по количеству ошибок (по возрастанию)
    const data = Object.values(byCriteria)
        .filter(item => item.zeroCount > 0)
        .sort((a, b) => b.zeroCount - a.zeroCount);
    // Подготавливаем данные для Chart.js
    setChartData({
      labels: data.map(item => item.name),
      datasets: [
        {
          label: 'Количество ошибок',
          data: data.map(item => item.zeroCount),
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
          borderRadius: 4
        }
      ]
    });
  }, [assessments, criteria, fieldOptions, criteria]);

  const options = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Количество ошибок: ${context.raw}`;
          }
        }
      },
      datalabels: {
        anchor: 'center',
        align: 'right',
        color: '#333',
        font: {
          weight: 'bold',
          size: 14
        },
        formatter: (value) => value,
        offset: 4,
        display: true
      }
    },
    scales: {
      y: {
        // Настройка для отображения до 10 элементов одновременно
        afterFit: function(axis) {
          // Вычисляем необходимую ширину на основе длины самых длинных меток
          let maxWidth = 450; // Достаточная ширина для длинных названий
          const labels = axis.chart.data.labels || [];

          labels.forEach(label => {
            // Оцениваем ширину текста
            const estimatedWidth = Math.min(label.length * 12, 450); // 12px на символ, максимум 800px
            maxWidth = Math.max(maxWidth, estimatedWidth);
          });
          
          axis.width = maxWidth;
        },
        beginAtZero: false,
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#333',
          callback: function(value, index, values) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            
            // Максимальная длина строки (увеличено для лучшего отображения)
            const maxLineLength = 70;
            
            // Если название короче или равно maxLineLength, возвращаем как есть
            if (label.length <= maxLineLength) {
              return label;
            }
            
            // Разбиваем название на части по словам, если возможно
            const words = label.split(' ');
            const lines = [];
            let currentLine = '';
            
            words.forEach(word => {
              // Проверяем, помещается ли слово в текущую строку
              if ((currentLine + ' ' + word).trim().length <= maxLineLength) {
                currentLine = (currentLine + ' ' + word).trim();
              } else {
                // Если текущая строка не пустая, добавляем ее
                if (currentLine) {
                  lines.push(currentLine);
                }
                // Начинаем новую строку
                currentLine = word;
              }
            });
            
            // Добавляем последнюю строку
            if (currentLine) {
              lines.push(currentLine);
            }
            
            // Возвращаем все строки без обрезания
            return lines;
          }
        }
      },
      x: {
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: 14
          },
          color: '#666',
          stepSize: 1,
          precision: 0
        },
        title: {
          display: true,
          text: 'Количество ошибок',
          font: {
            size: 16,
            weight: 'bold'
          },
          color: '#666'
        }
      }
    }
  };

  return (
    <div className="chart-card">
      <h3>Количество ошибок по критериям</h3>
      {Object.keys(chartData).length === 0 ? (
        <div className="no-data">Нет данных для отображения</div>
      ) : (
        <div className="chart-container" style={{ padding: '20px', height: '600px', overflow: 'auto' }}>
          <Bar data={chartData} options={options} />
        </div>
      )}
    </div>
  );
};

export default AssessmentsByCriteriaChart;